
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface BuyerProfileData {
  name: string;
  location: string;
  bio: string;
  profileImage: string;
  locationCoordinates: [number, number] | null;
}

export const useBuyerProfile = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState<BuyerProfileData>({
    name: '',
    location: '',
    bio: '',
    profileImage: '',
    locationCoordinates: null
  });

  // Load profile data from database
  useEffect(() => {
    const loadProfileData = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('first_name, last_name, location, description, profile_image_url, location_coordinates')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error loading profile:', error);
          return;
        }

        if (data) {
          // Parse location coordinates with proper type checking
          let coordinates: [number, number] | null = null;
          if (data.location_coordinates && Array.isArray(data.location_coordinates) && data.location_coordinates.length === 2) {
            const [lng, lat] = data.location_coordinates;
            if (typeof lng === 'number' && typeof lat === 'number') {
              coordinates = [lng, lat];
            }
          }

          setFormData({
            name: `${data.first_name || ''} ${data.last_name || ''}`.trim(),
            location: data.location || '',
            bio: data.description || '',
            profileImage: data.profile_image_url || '',
            locationCoordinates: coordinates
          });
        }
      } catch (error) {
        console.error('Error loading profile data:', error);
      }
    };

    loadProfileData();
  }, [user]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleLocationChange = (location: string, coordinates?: [number, number]) => {
    setFormData(prev => ({ 
      ...prev, 
      location,
      locationCoordinates: coordinates || null
    }));
    setHasChanges(true);
  };

  const handleImageUpdate = (url: string) => {
    setFormData(prev => ({ ...prev, profileImage: url }));
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const updateData = {
        first_name: firstName,
        last_name: lastName,
        location: formData.location,
        description: formData.bio,
        profile_image_url: formData.profileImage,
        location_coordinates: formData.locationCoordinates,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);

      if (error) {
        throw error;
      }

      setHasChanges(false);
      toast({
        title: "Success",
        description: "Profile updated successfully.",
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    loading,
    hasChanges,
    handleInputChange,
    handleLocationChange,
    handleImageUpdate,
    handleSaveChanges
  };
};
